import { getDocs, collection, doc, getDoc, deleteDoc } from "firebase/firestore";
import { db } from '../../../../../../firebase/config';
import { fetchCompaniesData } from '../../../../../companies/service/companiesService';
import type { Company } from '../../../../../companies/service/companiesService';
import { fetchChangeOfVendorBankId, fetchBranches, createChangeOfVendor, checkChangeOfVendorConflict, checkExistingRequest, type Branch } from '../../../service/changeOfVendorService';

export type DeployedCompany = {
    deploymentId: string;
    company: Company;
    companyType: string;
};

export type ChangeOfVendorFormData = {
    branchId: string;
    companiesId: string;
};

/**
 * Fetch available branches for the current user
 */
export async function fetchAvailableBranches(uid: string, isBankUser: boolean = false): Promise<Branch[]> {
    const bankId = await fetchChangeOfVendorBankId(uid, isBankUser);
    if (!bankId) {
        return [];
    }
    return await fetchBranches(bankId, isBankUser ? undefined : uid);
}

/**
 * Fetch available companies for the current user
 */
export async function fetchAvailableCompanies(uid: string, isBankUser: boolean = false): Promise<Company[]> {
    return await fetchCompaniesData(uid, isBankUser);
}

export type DeployedCompanyWithRequest = DeployedCompany & {
    hasPendingRequest?: boolean;
    existingRequestId?: string;
    existingRequestDocId?: string;
    existingRequestStatus?: string;
};

/**
 * Fetch deployed companies for a specific branch with request status
 */
export async function fetchDeployedCompaniesForBranch(
    uid: string,
    isBankUser: boolean,
    branchId: string
): Promise<DeployedCompanyWithRequest[]> {
    try {
        const bankId = await fetchChangeOfVendorBankId(uid, isBankUser);
        if (!bankId) {
            return [];
        }

        // Fetch all deployments for this branch
        const deploymentsSnap = await getDocs(collection(db, "banks", bankId, "companyDeployment"));
        const deployments = deploymentsSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter((d: any) => d.branchId === branchId);

        // Fetch all companies
        const companies = await fetchCompaniesData(uid, isBankUser);

        // Enrich deployments with company data and check for existing requests
        // First, fetch all requests for this branch to check status
        const requestsSnap = await getDocs(collection(db, "banks", bankId, "changeOfVendor"));
        const allRequests = requestsSnap.docs.map(doc => ({
            id: doc.id,
            bankId,
            ...doc.data()
        })) as any[];

        const deployedCompanies: DeployedCompanyWithRequest[] = [];
        for (const deployment of deployments) {
            const company = companies.find(c => c.id === (deployment as any).companiesId);
            if (company) {
                const companyType = company.companyType || 'Unknown';
                // Check if there's an existing request for this branch and company type (any status)
                // Find request that matches branch and company type
                const existingRequest = allRequests.find(req => {
                    if (req.branchId === branchId) {
                        // Find the company for this request
                        const reqCompany = companies.find(c => c.id === req.companiesId);
                        if (reqCompany) {
                            const reqCompanyType = reqCompany.companyType || 'Unknown';
                            return reqCompanyType === companyType;
                        }
                    }
                    return false;
                });
                
                // Only block if status is Pending or Approved
                const shouldBlock = existingRequest && (existingRequest.status === 'Pending' || existingRequest.status === 'Approved');
                
                deployedCompanies.push({
                    deploymentId: deployment.id,
                    company,
                    companyType,
                    hasPendingRequest: shouldBlock,
                    existingRequestId: existingRequest?.requestId,
                    existingRequestDocId: existingRequest?.id,
                    existingRequestStatus: existingRequest?.status
                });
            }
        }

        return deployedCompanies;
    } catch (error) {
        console.error('Error fetching deployed companies:', error);
        return [];
    }
}

/**
 * Fetch available companies filtered by company type
 */
export async function fetchCompaniesByType(
    uid: string,
    isBankUser: boolean,
    companyType: string
): Promise<Company[]> {
    const allCompanies = await fetchAvailableCompanies(uid, isBankUser);
    return allCompanies.filter(company => company.companyType === companyType);
}

/**
 * Cancel a change of vendor request
 */
export async function cancelChangeOfVendorRequest(
    uid: string,
    isBankUser: boolean,
    requestDocId: string
): Promise<void> {
    try {
        const bankId = await fetchChangeOfVendorBankId(uid, isBankUser);
        if (!bankId) {
            throw new Error('Bank ID not found');
        }
        
        // Update status to 'Cancelled' instead of deleting
        const { updateChangeOfVendorStatus } = await import('../../../service/changeOfVendorService');
        await updateChangeOfVendorStatus(bankId, requestDocId, 'Cancelled' as any);
        console.log('Request cancelled successfully:', requestDocId);
    } catch (error) {
        console.error('Error cancelling request:', error);
        throw error;
    }
}

/**
 * Submit a new change of vendor request
 */
export async function submitChangeOfVendor(
    uid: string,
    isBankUser: boolean,
    branchId: string,
    companiesId: string,
    skipConflictCheck: boolean = false,
    requestDate?: Date,
    deploymentDate?: Date
): Promise<{ success: boolean; conflict?: { existingCompany: Company } }> {
    try {
        console.log('submitChangeOfVendor called with:', { uid, isBankUser, branchId, companiesId, skipConflictCheck });
        
        const bankId = await fetchChangeOfVendorBankId(uid, isBankUser);
        if (!bankId) {
            throw new Error('Bank ID not found');
        }

        console.log('Bank ID found:', bankId);

        // Check for conflicts only if not skipping
        if (!skipConflictCheck) {
            const conflictCheck = await checkChangeOfVendorConflict(bankId, branchId, companiesId);
            console.log('Conflict check result:', conflictCheck);
            
            if (conflictCheck.hasConflict) {
                return {
                    success: false,
                    conflict: {
                        existingCompany: conflictCheck.existingCompany!
                    }
                };
            }
        }

               // Create change of vendor request
               console.log('Creating change of vendor request...');
               const requestId = await createChangeOfVendor(bankId, branchId, companiesId, uid, requestDate, deploymentDate);
               console.log('Change of vendor request created with ID:', requestId);
        
        return { success: true };
    } catch (error: any) {
        console.error('Error submitting change of vendor:', error);
        console.error('Error details:', {
            message: error.message,
            code: error.code,
            stack: error.stack
        });
        throw error;
    }
}

